import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Users, AlertTriangle, CheckCircle2, XCircle, Share2, Gauge } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useApp } from "@/lib/i18n";

const ICON = {
  quota: [Gauge, "text-amber-600 bg-amber-50"], meeting_done: [CheckCircle2, "text-emerald-600 bg-emerald-50"],
  meeting_failed: [XCircle, "text-red-600 bg-red-50"], meeting_shared: [Share2, "text-sky-600 bg-sky-50"],
  member_removed: [AlertTriangle, "text-red-600 bg-red-50"],
};
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  return m < 1 ? "baru" : m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} j` : `${Math.round(m / 1440)} h`;
};

export const NotificationBell = ({ testId = "notification-bell" }) => {
  const { L } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, [load]);
  const readAll = () => api.post("/notifications/read", {}).then(load);
  const openItem = (n) => {
    if (!n.read) api.post("/notifications/read", { ids: [n.id] }).then(load);
    setOpen(false);
    navigate(n.link || "/app");
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative grid h-9 w-9 place-items-center rounded-xl text-slate-600 hover:bg-slate-100" aria-label={L("Notifikasi", "Notifications")} data-testid={testId}>
          <Bell size={18} />
          {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid={`${testId}-count`}>{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] max-w-[calc(100vw-1.5rem)] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-semibold">{L("Notifikasi", "Notifications")}</p>
          {data.unread > 0 && <button onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-violet-700" data-testid="notifications-read-all"><CheckCheck size={14} /> {L("Tandakan semua dibaca", "Mark all read")}</button>}
        </div>
        <div className="slim-scroll max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notifications-empty">{L("Tiada notifikasi lagi.", "No notifications yet.")}</p>}
          {data.items.map((n) => {
            const [Icon, cls] = ICON[n.type] || [Users, "text-violet-600 bg-violet-50"];
            return (
              <button key={n.id} onClick={() => openItem(n)} className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-violet-50/40"}`} data-testid={`notification-${n.id}`}>
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${cls}`}><Icon size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2"><span className="truncate text-sm font-semibold">{n.title}</span><span className="shrink-0 text-[11px] text-slate-400">{ago(n.created_at)}</span></span>
                  <span className="mt-0.5 line-clamp-2 block text-xs text-slate-600">{n.body}</span>
                </span>
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-violet-600" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
